import {
  defaults,
  each,
  filter,
  first,
  get,
  includes,
  intersection,
  isObjectLike,
  sortBy,
} from 'lodash'
import moment from 'moment'

import {
  AddressQuestion,
  AutocompleteQuestion,
  BodySelectorQuestion,
  BooleanQuestion,
  CheckboxQuestion,
  DateQuestion,
  DropdownQuestion,
  FileQuestion,
  ImageQuestion,
  LabelOnlyQuestion,
  MeasurementQuestion,
  MultiDropdownQuestion,
  PatientIntakeFormData, PhoneQuestion,
  Question,
  Questions,
  RadioQuestion,
  ScoreResultQuestion,
  SliderQuestion,
  TableQuestion,
  TextQuestion } from '../classes'
import { AppointmentModel } from '../model/appointment'
import { Clinic } from '../model/clinic'
import { IntakeModel } from '../model/intake'
import { IEnabledIntakeSections, IQuestion, ISection, QuestionType } from '../model/question'
import { checkAgeRestriction } from './'

export interface IntakeSection {
  label: string
  path: string
  sectionId: string
  default: boolean
  questions?: Questions
  beforeLeave?: Function
  requirements?: ISectionRequirements
  isHidden?: boolean
  isDisabled?: boolean
}

interface ISectionRequirements {
  consents?: string[]
  sexes?: string[]
  age?: ISectionRequirementsAge
}

interface ISectionRequirementsAge {
  comparison: '<=' | '>=' | '=' | 'BETWEEN'
  months?: number
  min?: number
  max?: number
}

interface SortableIntakeSection extends IntakeSection {
  order: number
}

export function getIntakeSections(
  clinic: Clinic,
  appointment: AppointmentModel,
  data?: PatientIntakeFormData,
  intakeSections?: ISection[],
  questionsBySections?: ISection[],
  enabledSections?: IEnabledIntakeSections,
): IntakeSection[] {
  function isEnabled(section: string) {
    return (
      clinic.options[section] &&
      (!enabledSections || enabledSections.includes(section))
    )
  }

  if (clinic.options.intakeOrder == null) {
    clinic.options.intakeOrder = {}
  }

  let sections: SortableIntakeSection[] = [
    {
      label: 'patient-intake.welcome.section-name',
      path: 'welcome',
      default: true,
      sectionId: 'Welcome',
      isHidden: false,
      order: clinic.options.intakeOrder['Welcome'] ?? 1,
    },
  ]

  if (isEnabled('inPatientContact')) {
    sections.push({
      label: 'patient-intake.patient-information.section-name',
      path: 'contact',
      default: true,
      sectionId: 'Patient Information',
      isHidden: false,
      order: clinic.options.intakeOrder['Patient Information'] ?? 2,
    })
  }

  if (isEnabled('inAutoAccidentRelated')) {
    sections.push({
      label: 'patient-intake.accident-section.section-name',
      path: 'accident',
      default: true,
      sectionId: 'Accident Information',
      isHidden: false,
      order: clinic.options.intakeOrder['Accident Information'] ?? 4,
    })
  }

  if (isEnabled('inEmployer')) {
    sections.push({
      label: 'patient-intake.employer-section.section-name',
      path: 'employer',
      default: true,
      sectionId: 'Employer',
      isHidden: false,
      order: clinic.options.intakeOrder['Employer'] ?? 5,
    })
  }

  if (isEnabled('inPCP')) {
    sections.push({
      label: 'patient-intake.pcp-section.section-name',
      path: 'pcp',
      default: true,
      sectionId: 'Primary Care Physician',
      isHidden: false,
      order: clinic.options.intakeOrder['Primary Care Physician'] ?? 6,
    })
  }

  if (isEnabled('inEmergencyContact')) {
    sections.push({
      label: 'patient-intake.emergency-contact-section.section-name',
      path: 'emergency',
      default: true,
      sectionId: 'Emergency Contact',
      isHidden: false,
      order: clinic.options.intakeOrder['Emergency Contact'] ?? 7,
    })
  }

  // insert custom sections
  if (intakeSections) {
    intakeSections.forEach((section) => {
      if (
        isObjectLike(section) &&
        getQuestionsBySection(
          section.label,
          appointment,
          data,
          questionsBySections,
        ).length
      ) {
        sections.push({
          label: section.label,
          path: section.id,
          default: false,
          sectionId: section.label,
          order: section.order,
          requirements: section.requirements,
          // generating pdfs is not supported from clinician-console
          isDisabled: section.printInto === 'pdf',
          isHidden: isHiddenSection(
            section.requirements,
            new IntakeModel(data),
          ),
        })
      }
    })
  }

  sections = sortBy(sections, 'order')

  return sections.map((section, index) => {
    section.order = index
    return section
  })
}

export function getQuestionsBySection(
  label: string,
  appointment: AppointmentModel | undefined,
  intakeData: PatientIntakeFormData,
  questionsByLeg: any,
) {
  const leg = first(filter(questionsByLeg, { label }))

  if (leg && intakeData) {
    const consentsSelected: string[] = []
    each(intakeData.consented, (consented, consentId) => {
      if (consented) {
        consentsSelected.push(consentId)
      }
    })

    const questionsFiltered = filter(leg.questions, (question) => {
      const questionRequirements = question.requirements

      // determine if this question has consent requirement
      if (
        get(questionRequirements, 'consents') &&
        questionRequirements.consents.length !== 0 &&
        intersection(questionRequirements.consents, consentsSelected).length ===
          0
      ) {
        return false
      }

      // determine if this question has sex requirement
      if (
        get(questionRequirements, 'sexes') &&
        questionRequirements.sexes.length !== 0 &&
        intakeData.sex &&
        !includes(questionRequirements.sexes, intakeData.sex)
      ) {
        return false
      }
      // determine if this question has sex requirement
      if (get(questionRequirements, 'age.comparison')) {
        // when we don't have an appointment loaded or this is for an order,
        // use today's date to determine the patient's age
        const dos = appointment?.startAt
          ? moment.utc(appointment.startAt)
          : moment()
        const ageCheckPassed = checkAgeRestriction(
          intakeData.dob,
          questionRequirements.age,
          dos,
        )
        if (!ageCheckPassed) {
          return false
        }
      }

      return true
    })

    const questions = getNestedQuestions(questionsFiltered)

    return questions
  }
  return []
}

export function isHiddenSection(
  sectionRequirements: ISectionRequirements,
  intake: IntakeModel,
) {
  if (intake) {
    const requirements = {
      consents: intake.consented,
      sex: intake.sex,
    }

    const consentsSelected: string[] = []
    each(requirements.consents, (consented, consentId) => {
      if (consented) {
        consentsSelected.push(consentId)
      }
    })

    // determine if this question has consent requirement
    if (
      get(sectionRequirements, 'consents') &&
      sectionRequirements.consents.length !== 0 &&
      intersection(sectionRequirements.consents, consentsSelected).length !==
        sectionRequirements.consents.length
    ) {
      return true
    }

    // determine if this question has sex requirement
    if (
      get(sectionRequirements, 'sexes') &&
      sectionRequirements.sexes.length !== 0 &&
      !includes(sectionRequirements.sexes, requirements.sex)
    ) {
      return true
    }

    // determine if this question has age requirement
    if (
      get(sectionRequirements, 'age.comparison') &&
      !checkAgeRestriction(intake.dob, sectionRequirements.age)
    ) {
      return true
    }
  }

  return false
}

export function mashQuestions(sections: ISection[]) {
  const questions: IQuestion[] = []

  const recursive = (qs: IQuestion[]) => {
    questions.push(...qs)

    for (const question of qs) {
      if (question.questions) {
        recursive(question.questions)
      }
    }
  }

  for (const section of sections) {
    if (section.questions != null) {
      recursive(section.questions)
    }
  }

  return questions
}

function getNestedQuestions(stepQuestions: IQuestion[]): Questions {
  return stepQuestions.map((options) => {
    if (options.questions) {
      // force in the ifQuestions to pass them to BooleanQuestion
      ;(options as any).ifQuestions = getNestedQuestions(options.questions)
    }
    return createQuestion(options)
  })
}

function createQuestion(questionData: IQuestion) {
  const ui = get(questionData, 'options.ui', '')

  defaults(questionData, { flex: '100' })

  switch (questionData.type) {
    case QuestionType.Boolean:
      return new BooleanQuestion(questionData)
    case QuestionType.Checkbox:
      return new CheckboxQuestion(questionData)
    case QuestionType.Radio:
      return new RadioQuestion(questionData)
    case QuestionType.Date:
      return new DateQuestion(questionData)
    case QuestionType.Address:
      return new AddressQuestion(questionData)
    case QuestionType.Measurement:
      return new MeasurementQuestion(questionData)
    case QuestionType.Phone:
      return new PhoneQuestion(questionData)
    case QuestionType.File:
      return new FileQuestion(questionData)
    case QuestionType.Image:
      return new ImageQuestion(questionData)
    case QuestionType.BodySelector:
      return new BodySelectorQuestion(questionData)
    case QuestionType.Label:
      return new LabelOnlyQuestion(questionData)
    case QuestionType.ScoreResult:
      return new ScoreResultQuestion(questionData)
    case QuestionType.Table:
      return new TableQuestion(questionData)
    case QuestionType.Slider:
      return new SliderQuestion(questionData)
    case QuestionType.Dropdown:
      switch (ui) {
        case 'autocomplete':
          return new AutocompleteQuestion(questionData)
        case 'multiple':
          return new MultiDropdownQuestion(questionData)
        default:
          // default single
          return new DropdownQuestion(questionData)
      }
    default:
      return ui === 'textarea'
        ? new TextQuestion(questionData)
        : new Question(questionData)
  }
}
